/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useMemo, useEffect } from 'react'
import cn from 'classnames'
import classes from '../../style/index.styl'


export interface ToolbarZoomInputProps {
  /**
   * 缩放比例
   */
  scale: number
  /**
   * 最小的缩放比例
   * @default 0.50
   */
  MIN_SCALING?: number
  /**
   * 最大的缩放比例
   * @default 4.00
   */
  MAX_SCALING?: number
  /**
   * 缩放的步长
   * @default 0.1
   */
  ZOOM_STEP?: number
  /**
   * 缩放比例下拉框的可选项
   * @default [ 0.50, 0.75, 1.00, 1.25, 1.50, 2.00, 3.00, 4.00 ]
   */
  scaleOptions?: number[]
  /**
   * 缩放比例发生变化
   */
  onScaleChange: (scale: number) => void
}


export type ScaleOptionType = 'page-width' | 'custom' | string


export function ToolbarZoomInput(props: ToolbarZoomInputProps & React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
  const {
    scale,
    MIN_SCALING= 0.50,
    MAX_SCALING = 4.00,
    ZOOM_STEP = 0.1,
    scaleOptions: defaultScaleOptions = [
      0.50, 0.75, 1.00, 1.25, 1.50, 2.00, 3.00, 4.00
    ],
    onScaleChange,
    ...restProps
  } = props

  const [scaleOptionSelected, setScaleOptionSelected] = useState<ScaleOptionType>('100%')
  const scaleOptions = useMemo<React.ReactElement[]>(() => {
    return defaultScaleOptions.map(o => {
      const value = o.toFixed(2)
      const percent = (o * 100).toFixed(0)
      return (
        <option
          key={ percent }
          value={ value }
          data-l10n-id="page_scale_percent"
          data-l10n-args={ `{ "scale": ${ percent } }` }
        >
          { percent }%
        </option>
      )
    })
  }, [defaultScaleOptions])

  // watch props.scale
  useEffect(() => {
    setScaleOptionSelected('custom')
  }, [defaultScaleOptions, scale])

  const handleScaleOptionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value: string = e.target.value
    setScaleOptionSelected(value)
    const newScale = Number(value)
    if (Number.isNaN(newScale)) return
    onScaleChange(newScale)
  }

  const handleZoomOut = () => {
    const newScale = Math.max(scale - ZOOM_STEP, MIN_SCALING)
    setScaleOptionSelected('custom')
    onScaleChange(newScale)
  }

  const handleZoomIn = () => {
    const newScale = Math.min(scale + ZOOM_STEP, MAX_SCALING)
    setScaleOptionSelected('custom')
    onScaleChange(newScale)
  }

  return (
    <div { ...restProps }>
      <div className={ cn(classes.splitToolbarButton) }>
        <button
          title="缩小"
          tabIndex={ 21 }
          data-l10n-id="zoom_out"
          className={ cn(classes.zoomOut, classes.toolbarButton) }
          onClick={ handleZoomOut }
        >
          <span data-l10n-id="zoom_out_label">缩小</span>
        </button>
        <div className={ classes.splitToolbarButtonSeparator } />
        <button
          title="放大"
          tabIndex={ 22 }
          data-l10n-id="zoom_in"
          className={ cn(classes.zoomIn, classes.toolbarButton) }
          onClick={ handleZoomIn }
        >
          <span data-l10n-id="zoom_in_label">放大</span>
        </button>
      </div>
      <span className={ cn(classes.scaleSelectContainer, classes.dropdownToolbarButton) }>
        <select
          title="缩放"
          tabIndex={ 23 }
          data-l10n-id="zoom"
          value={ scaleOptionSelected }
          onChange={ handleScaleOptionChange }
        >
          <option value="custom" data-l10n-id="page_scale_percent" disabled hidden>
            { (scale * 100).toFixed(2) }%
          </option>
          { scaleOptions }
        </select>
      </span>
    </div>
  )
}


export default ToolbarZoomInput
